/**
 * ChipMenu alignment relative to its anchor.
 *   - `left`   (default) — panel's left edge aligns with the anchor's left.
 *               Used by sort/folder/tag toolbar menus.
 *   - `right`  — panel's right edge aligns with the anchor's right. Used by
 *               row-actions kebabs that hang off the right edge of a table.
 *   - `center` — panel is centred horizontally below the anchor. Used by
 *               the Process editor's crumb dropdown and any other menu that
 *               opens directly under a centred trigger.
 */
export const CHIP_MENU_ALIGN_LEFT = 'left';
export const CHIP_MENU_ALIGN_RIGHT = 'right';
export const CHIP_MENU_ALIGN_CENTER = 'center';

export type ChipMenuAlign =
  | typeof CHIP_MENU_ALIGN_LEFT
  | typeof CHIP_MENU_ALIGN_RIGHT
  | typeof CHIP_MENU_ALIGN_CENTER;

/**
 * ChipMenu item tones. Mirrors the design's `.chip-menu__item--accent` /
 * `--danger` / `--archive` modifiers so callers don't sprinkle hex.
 */
export const CHIP_MENU_PLACEMENT_BOTTOM = 'bottom';
export const CHIP_MENU_PLACEMENT_TOP = 'top';

export type ChipMenuPlacement =
  | typeof CHIP_MENU_PLACEMENT_BOTTOM
  | typeof CHIP_MENU_PLACEMENT_TOP;

export const CHIP_MENU_ITEM_TONE_DEFAULT = 'default';
export const CHIP_MENU_ITEM_TONE_GHOST = 'ghost';
export const CHIP_MENU_ITEM_TONE_ACCENT = 'accent';
export const CHIP_MENU_ITEM_TONE_DANGER = 'danger';
export const CHIP_MENU_ITEM_TONE_ARCHIVE = 'archive';

export type ChipMenuItemTone =
  | typeof CHIP_MENU_ITEM_TONE_DEFAULT
  | typeof CHIP_MENU_ITEM_TONE_GHOST
  | typeof CHIP_MENU_ITEM_TONE_ACCENT
  | typeof CHIP_MENU_ITEM_TONE_DANGER
  | typeof CHIP_MENU_ITEM_TONE_ARCHIVE;

/**
 * ChipMenu item visual variant.
 *   - `item`   (default) — flat row with coloured text per tone. Used for
 *               typical menu options (Sort by, Move to folder, etc.).
 *   - `button` — full pill button. The tone's colour fills the background
 *               and a contrast colour is used for the label/icon. Use this
 *               when the row is a *primary action* inside the menu (e.g.
 *               "+ New process" in the Process editor's crumb dropdown,
 *               "+ New folder" in the folder picker). Styling lives inside
 *               this component — consumers should NEVER restyle a chip-menu
 *               item from the application layer.
 */
export const CHIP_MENU_ITEM_VARIANT_ITEM = 'item';
export const CHIP_MENU_ITEM_VARIANT_BUTTON = 'button';

export type ChipMenuItemVariant =
  | typeof CHIP_MENU_ITEM_VARIANT_ITEM
  | typeof CHIP_MENU_ITEM_VARIANT_BUTTON;

/**
 * How the active row is highlighted.
 *   - `pill`   (default) — coloured text + tinted pill background. Matches
 *               the standard Material chip-menu treatment used by sort,
 *               folder, and tag pickers.
 *   - `subtle` — coloured text only (uses `onSurface`), transparent
 *               background, very subtle hover. Used by menus where the
 *               row colour is the only active indicator (e.g. the Process
 *               editor's crumb dropdown). Set this on the ChipMenu root and
 *               every Item inside inherits it via context.
 */
export const CHIP_MENU_ACTIVE_TREATMENT_PILL = 'pill';
export const CHIP_MENU_ACTIVE_TREATMENT_SUBTLE = 'subtle';

export type ChipMenuActiveTreatment =
  | typeof CHIP_MENU_ACTIVE_TREATMENT_PILL
  | typeof CHIP_MENU_ACTIVE_TREATMENT_SUBTLE;
